import { useLocation, useNavigate } from "react-router-dom";
import Footer from "../Footer";
import Navbar from "../Navbar";
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const AddOpeningCount = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const today = new Date().toISOString().split("T")[0];
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const { inventoryData, selectedOutlet, accessToken } = location.state || {};
    const [editableData, setEditableData] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [startDate, setStartDate] = useState(today);

    const unitMappings = {
        kgVariations: ['kg', 'kgs', 'kilogram', 'kilograms'],
        gramVariations: ['g', 'gram', 'grams', 'gm', 'gms', 'grm', 'grms'],
        literVariations: ['l', 'ltr', 'ltrs', 'liter', 'liters', 'litre', 'litres'],
        milliliterVariations: ['ml', 'milliliter', 'milliliters', 'millilitre', 'millilitres', 'mls']
    };

    const handleDateChange = (e, type) => {
        const value = e.target.value;
        if (type === 'start') {
            setStartDate(value);
        }
    };

    useEffect(() => {
        if (accessToken !== "a7fK2pX9Zd") {
            navigate('/inventorycalculation');
        }
    }, [accessToken, navigate]);

    useEffect(() => {
        if (inventoryData?.summary) {
            const flattenedData = Object.entries(inventoryData.summary)
                .flatMap(([category, groups]) =>
                    Object.entries(groups)
                        .flatMap(([groupName, items]) =>
                            items.map(item => ({
                                ...item,
                                category,
                                groupName,
                                editableOpening: item.opening,
                                primaryQty: "0",
                                secondaryQty: "0",
                                unitdefinition: item.unitdefinition
                            }))
                        ));
            setEditableData(flattenedData);
        }
    }, [inventoryData]);

    const handlePrimaryChange = (index, value) => {
        const newData = [...editableData];
        newData[index].primaryQty = value === "" ? "" : value;
        newData[index].editableOpening = computeFinalQuantity(newData[index]);
        setEditableData(newData);
    };

    const handleSecondaryChange = (index, value) => {
        const newData = [...editableData];
        newData[index].secondaryQty = value === "" ? "" : value;
        newData[index].editableOpening = computeFinalQuantity(newData[index]);
        setEditableData(newData);
    };

    function computeFinalQuantity(item) {
        const primaryQty = parseFloat(item.primaryQty) || 0;
        const secondaryQty = parseFloat(item.secondaryQty) || 0;
        const unitSize = parseFloat(item.unitdefinition?.unit) || 1;
        const baseUom = (item.unitdefinition?.uom || '').toLowerCase();
        const itemUom = (item.uom || '').toLowerCase();
        const newUom = (item.new_uom || '').toLowerCase();

        const {
            kgVariations,
            gramVariations,
            literVariations,
            milliliterVariations
        } = unitMappings;

        const isMatch = (uom, variations) => variations.includes(uom);

        let primaryInBaseUnit, secondaryInBaseUnit;

        if (itemUom === baseUom) {
            primaryInBaseUnit = primaryQty;
        } else {
            primaryInBaseUnit = primaryQty * unitSize;
        }

        if (newUom === baseUom) {
            secondaryInBaseUnit = secondaryQty;
        } else {
            if (isMatch(baseUom, kgVariations) && isMatch(newUom, gramVariations)) {
                secondaryInBaseUnit = secondaryQty / 1000;
            } else if (isMatch(baseUom, literVariations) && isMatch(newUom, milliliterVariations)) {
                secondaryInBaseUnit = secondaryQty / 1000;
            } else {
                secondaryInBaseUnit = secondaryQty;
            }
        }

        const totalInBaseUnit = primaryInBaseUnit + secondaryInBaseUnit;

        if (itemUom !== baseUom) {
            return (totalInBaseUnit / unitSize);
        }

        return totalInBaseUnit;
    }

    const shouldShowSecondary = (item) => {
        const itemUom = (item.uom || '').toLowerCase().trim();
        const newUom = (item.new_uom || '').toLowerCase().trim();
        const defUom = (item.unitdefinition?.uom || '').toLowerCase().trim();

        if (!defUom) {
            return itemUom !== newUom;
        }

        return !(itemUom === newUom && newUom === defUom);
    };

    const handleSave = () => {
        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to save inventory data?</p>
                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        onClick={async () => {
                            toast.dismiss();
                            if (!editableData.length) return;

                            setIsSaving(true);
                            try {
                                const payload = {
                                    token: sessionStorage.getItem('token'),
                                    outlet: selectedOutlet,
                                    start_date: startDate,
                                    end_date: startDate,
                                    physical_added: false,
                                    items: editableData.map(item => ({
                                        name: item.name,
                                        type: item.category,
                                        group_name: item.groupName,
                                        uom: item.uom,
                                        new_uom: item.new_uom,
                                        opening_count: 0,
                                        total_received: 0,
                                        total_consumed: 0,
                                        closing_balance: parseFloat(item.editableOpening) || 0,
                                        physical_count: parseFloat(item.editableOpening) || 0
                                    }))
                                };

                                await axios.post(`${baseUrl}/save-inventory-snapshot`, payload);
                                toast.success('Opening counts saved successfully!');
                                navigate('/inventorycalculation');
                            } catch (error) {
                                console.error('Error saving opening counts:', error);
                                toast.error('Failed to save opening counts. Please try again.');
                            } finally {
                                setIsSaving(false);
                            }
                        }}
                    >
                        Save
                    </button>
                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => toast.dismiss()}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                toastId: 'save-opening-count'
            }
        );
    };

    return (
        <div className="flex flex-col min-h-screen">
            <Navbar />

            <div className="flex-grow">
                <div className="mx-auto max-w-6xl p-4 mt-3">
                    <h3 className="font-semibold text-xl">
                        <span className="text-gray-600">Opening Count for</span> {selectedOutlet}
                    </h3>

                    <div className="flex items-center gap-10 my-2">
                        <div className="flex items-center gap-3">
                            <input
                                type="date"
                                className="border p-1 rounded"
                                value={startDate}
                                onChange={(e) => handleDateChange(e, 'start')}
                            />
                        </div>
                    </div>

                    <div className="mt-3 rounded-md border overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm text-center">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Group Name</th>
                                    <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Item Name</th>
                                    <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Definition</th>
                                    <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider" colSpan="2">Primary Unit</th>
                                    <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider" colSpan="2">Secondary Unit</th>
                                </tr>
                                <tr>
                                    <th></th>
                                    <th></th>
                                    <th></th>
                                    <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-r">UOM</th>
                                    <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                                    <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-r">UOM</th>
                                    <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                                {editableData.length > 0 ? (
                                    editableData.map((item, index) => {
                                        const showSecondary = shouldShowSecondary(item);

                                        return (
                                            <tr key={`${item.category}-${item.groupName}-${item.name}-${index}`} className="hover:bg-gray-100">
                                                <td className="px-3 py-2">{item.groupName}</td>
                                                <td className="px-3 py-2">{item.name}</td>
                                                <td className="px-3 py-2">{item.unitdefinition?.unit || 1} ({item.unitdefinition?.uom || item.uom})</td>

                                                <td className="px-3 py-2 border-r">{item.uom}</td>
                                                <td className="px-3 py-2">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="any"
                                                        className="w-20 px-2 py-1 border rounded text-center"
                                                        value={item.primaryQty}
                                                        onChange={(e) => handlePrimaryChange(index, e.target.value)}
                                                    />
                                                </td>

                                                <td className="px-3 py-2 border-r">
                                                    {showSecondary ? item.new_uom : '-'}
                                                </td>
                                                <td className="px-3 py-2">
                                                    {showSecondary ? (
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            step="any"
                                                            className="w-20 px-2 py-1 border rounded text-center"
                                                            value={item.secondaryQty}
                                                            onChange={(e) => handleSecondaryChange(index, e.target.value)}
                                                        />
                                                    ) : (
                                                        '-'
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={8} className="px-3 py-4 text-gray-500">
                                            No data available
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {editableData.length > 0 && (
                        <div className="mt-4 flex justify-end">
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400"
                            >
                                {isSaving ? 'Saving...' : 'Save Opening Counts'}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default AddOpeningCount;